/**
 * One wording for membership benefits across the dashboard, widget and
 * webcomponents. A frequency "use" is one booking, so the noun is "class".
 */

export type FrequencyPeriod = "week" | "month" | "days";

export type FormattableBenefit = {
  type: "credit" | "unlimited" | "frequency";
  label?: string | null;
  creditsPerCycle?: number | null;
  usagesPerFrequency?: number | null;
  /** Unset = rolling "days" (benefits created before calendar periods). */
  frequencyPeriod?: FrequencyPeriod | null;
  frequencyDays?: number | null;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function frequencyParts(b: FormattableBenefit) {
  return {
    limit: b.usagesPerFrequency ?? 1,
    period: b.frequencyPeriod ?? "days",
    days: b.frequencyDays ?? 7,
  };
}

/** "2 classes per week", "10 credits", "Unlimited" — or the benefit's label. */
export function formatBenefit(b: FormattableBenefit): string {
  if (b.label) return b.label;
  if (b.type === "credit") return plural(b.creditsPerCycle ?? 0, "credit", "credits");
  if (b.type === "unlimited") return "Unlimited";

  const { limit, period, days } = frequencyParts(b);
  const classes = plural(limit, "class", "classes");
  if (period === "week") return `${classes} per week`;
  if (period === "month") return `${classes} per month`;
  return days === 1 ? `${classes} every day` : `${classes} every ${days} days`;
}

/** "1 of 2 used this week · resets Monday". `b` should carry any per-member override. */
export function formatFrequencyUsage(b: FormattableBenefit, used: number): string {
  const { limit, period, days } = frequencyParts(b);
  if (period === "week") return `${used} of ${limit} used this week · resets Monday`;
  if (period === "month") return `${used} of ${limit} used this month · resets on the 1st`;
  return `${used} of ${limit} used in the next ${days === 1 ? "day" : `${days} days`}`;
}

/** A frequency benefit's usage this period, as GET /widget/me/memberships returns it. */
export type FrequencyUsageDetail = { used: number; limit: number; periodStart: string; periodEnd: string };

/**
 * The member's "1 of 2 used this week" lines, one per frequency benefit the
 * payload has usage for. `limit` already carries any per-member override.
 */
export function frequencyUsageLines(sub: {
  membershipPlan?: { benefits?: Array<FormattableBenefit & { id: string }> | null } | null;
  frequencyUsages?: Record<string, FrequencyUsageDetail> | null;
}): string[] {
  const lines: string[] = [];
  for (const b of sub.membershipPlan?.benefits ?? []) {
    if (b.type !== "frequency") continue;
    const usage = sub.frequencyUsages?.[b.id];
    if (!usage) continue;
    const text = formatFrequencyUsage({ ...b, usagesPerFrequency: usage.limit }, usage.used);
    lines.push(b.label ? `${b.label}: ${text}` : text);
  }
  return lines;
}

/**
 * What a frequency benefit covers, for the checkout ("2 classes per week").
 * Deliberately no usage: the resolver's usagesInWindow counts the current
 * period, while the basket may hold a class in a later week or month that
 * the gate counts against that period instead.
 */
export function frequencyCoverageDetail(benefits: FormattableBenefit[] | null | undefined): string | null {
  const fb = benefits?.find((b) => b.type === "frequency");
  return fb ? formatBenefit({ ...fb, label: null }) : null;
}

/**
 * A benefit as one member gets it: their class-count override, and their
 * days override on a rolling benefit only — the same rule the checkout gate
 * applies, so what staff see matches what the member is refused on.
 */
export function withMemberOverrides<B extends FormattableBenefit>(
  b: B,
  memberRow: { usagesPerFrequencyOverride?: number | null; frequencyDaysOverride?: number | null } | null | undefined
): B {
  if (!memberRow) return b;
  const rolling = (b.frequencyPeriod ?? "days") === "days";
  return {
    ...b,
    usagesPerFrequency: memberRow.usagesPerFrequencyOverride ?? b.usagesPerFrequency,
    frequencyDays: rolling ? memberRow.frequencyDaysOverride ?? b.frequencyDays : b.frequencyDays,
  };
}
